import React, { useState, useEffect, useRef, useCallback } from "react";
import { CheckCircle2, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/api";
import { canonicalUrl, sortListings } from "./listings";
import { ShareCard } from "./ShareCard";
import { SHARE_SITE, tweetIntentUrl } from "./shareLinks";
// Side-effect import: attaches api.getConfirm + api.prewarmHealth onto the shared
// `api` object (api.js itself is owned by another workstream and must not change).
import "@/apiConfirm";

/**
 * P2 L4 — return confirmation poll.
 *
 * After a hosted-checkout redirect the page comes back with `?session_id=...`.
 * We poll the SERVER-VERIFIED endpoint api.getConfirm(sessionId) and never trust
 * any `status` query param or add a listing locally. Listings are owned solely by
 * api.getBoard(); the client never upserts on return.
 *
 * States (data-testids the browser gate keys off):
 *   - confirm-view        : always present on this view
 *   - confirm-poll        : present while we are still polling
 *   - confirm-status      : text reflecting pending | paid | unknown | timeout
 *   - confirm-paid        : shown only when the server confirms "paid"
 *   - confirm-timeout     : shown when the poll gives up (~8 min) or errors repeatedly
 *   - confirm-retry       : error / retry affordance (for unknown or timeout)
 */

// Exponential backoff: 2s, 4s, 8s, then cap at 10s. ~240 tries ≈ 8 minutes.
const BASE_INTERVAL = 2000;
const MAX_INTERVAL = 10000;
const MAX_TRIES = 240;

function backoffFor(tryCount) {
  return Math.min(MAX_INTERVAL, BASE_INTERVAL * 2 ** Math.max(0, tryCount - 1));
}

async function resolveShareListing() {
  let pendingUrl = "";
  try {
    pendingUrl = sessionStorage.getItem("gmh-pending-url") || "";
  } catch {
    pendingUrl = "";
  }
  try {
    const board = await api.getBoard();
    const listings = sortListings(board.listings || []);
    if (!listings.length) return null;
    if (pendingUrl) {
      const key = canonicalUrl(pendingUrl);
      const hit = listings.find((l) => canonicalUrl(l.url) === key);
      if (hit) {
        return { listing: hit, rank: listings.findIndex((l) => l.id === hit.id) + 1 };
      }
    }
    if (listings.length === 1) return { listing: listings[0], rank: 1 };
  } catch {
    /* board may still be waking; share CTA still renders a generic tweet */
  }
  return null;
}

export const Confirmation = ({ sessionId, onPaid, onDismiss }) => {
  const [phase, setPhase] = useState("polling"); // polling | paid | unknown | timeout
  const [share, setShare] = useState(null);

  const timerRef = useRef(null);
  const triesRef = useRef(0);
  const doneRef = useRef(false);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  const stop = useCallback(() => {
    doneRef.current = true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const finishPaid = useCallback(() => {
    stop();
    setPhase("paid");
    // Task 2: flag that a real payment just succeeded so the board can play
    // the boost + bid blast on the next mount. One-shot; cleared on board mount.
    try {
      window.sessionStorage.setItem("gmh-just-paid", "1");
    } catch {
      /* storage may be unavailable; boost is cosmetic */
    }
    // Refresh the board from the server (the webhook may have promoted the listing).
    if (onPaidRef.current) {
      try {
        onPaidRef.current();
      } catch {
        /* surfacing handled by caller */
      }
    }
    resolveShareListing().then((hit) => {
      if (hit) setShare(hit);
    });
    // NOTE: do NOT strip the URL here. Leaving confirm/session_id in place keeps
    // App rendering the Confirmation view ("Your listing is live") until the user
    // explicitly clicks "View the board" (onDismiss strips the params).
  }, [stop]);

  useEffect(() => {
    if (!sessionId) {
      setPhase("unknown");
      return undefined;
    }

    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled || doneRef.current) return;
      const next = triesRef.current + 1;
      triesRef.current = next;
      if (next >= MAX_TRIES) {
        stop();
        setPhase("timeout");
        return;
      }
      timerRef.current = setTimeout(poll, backoffFor(next));
    };

    const poll = async () => {
      if (cancelled || doneRef.current) return;
      try {
        const { status } = await api.getConfirm(sessionId);
        if (cancelled || doneRef.current) return;
        if (status === "paid") {
          finishPaid();
          return;
        }
        if (status === "failed") {
          stop();
          setPhase("failed");
          return;
        }
        if (status === "unknown") {
          stop();
          setPhase("unknown");
          return;
        }
        // status === "pending" → keep polling
        scheduleNext();
      } catch {
        // Transport error. If we've already burnt most of our budget, give up
        // honestly rather than claiming success; otherwise retry with backoff.
        if (cancelled || doneRef.current) return;
        if (triesRef.current >= MAX_TRIES) {
          stop();
          setPhase("timeout");
          return;
        }
        scheduleNext();
      }
    };

    // Kick off the first poll immediately.
    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      data-testid="confirm-view"
      className="space-bg noise min-h-screen flex items-center justify-center px-4 py-16"
    >
      <div className="panel corner-tick w-full max-w-lg p-8 text-center">
        {phase === "polling" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-gmh-cyan" />
            <h1 className="mt-5 text-xl font-bold tracking-wide text-white">Verifying your payment…</h1>
            <p className="mono mt-2 text-xs tracking-[0.25em] text-slate-500">POLLING SECURE UPLINK</p>
            <p className="mt-3 text-sm text-slate-400">
              Hang tight — we're confirming your fuel transfer with the server. This usually takes a few seconds.
            </p>
            <span data-testid="confirm-poll" className="mono mt-4 inline-block text-[10px] tracking-[0.3em] text-gmh-cyan">
              ● LIVE POLL
            </span>
            <div data-testid="confirm-status" className="sr-only">pending</div>
            {onDismiss && (
              <button
                type="button"
                data-testid="confirm-cancel"
                onClick={onDismiss}
                className="mono mt-6 flex items-center gap-2 rounded-md border border-slate-500/40 bg-slate-500/10 px-4 py-2 text-[11px] font-semibold tracking-wide text-slate-300 transition-colors hover:bg-slate-500/20"
              >
                Cancel
              </button>
            )}
          </>
        )}

        {phase === "paid" && (
          <>
            <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
            <h1 className="mt-5 text-2xl font-bold tracking-wide text-white">Your listing is live</h1>
            <p className="mt-3 text-sm text-slate-300">
              Payment confirmed by the server. Your link is now on the board — thank you for fueling the rescue.
            </p>
            <div data-testid="confirm-paid" className="mono mt-4 inline-block rounded-md border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 text-[11px] tracking-[0.3em] text-emerald-300">
              ✓ CONFIRMED
            </div>
            <div data-testid="confirm-status" className="sr-only">paid</div>
            <div data-testid="confirm-share" className="mt-6 text-left">
              {share ? (
                <ShareCard listing={share.listing} rank={share.rank} />
              ) : (
                <a
                  data-testid="share-tweet"
                  href={tweetIntentUrl({ shareUrl: SHARE_SITE })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mono inline-flex items-center justify-center rounded-md border border-gmh-cyan/40 bg-gmh-cyan/10 px-4 py-2 text-[11px] font-semibold tracking-wide text-gmh-cyan transition-colors hover:bg-gmh-cyan/20"
                >
                  Share on X — I fueled Sam
                </a>
              )}
            </div>
            {onDismiss && (
              <button
                type="button"
                data-testid="confirm-done"
                onClick={onDismiss}
                className="mono mt-6 flex items-center gap-2 rounded-md border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 text-[11px] font-semibold tracking-wide text-emerald-300 transition-colors hover:bg-emerald-400/20"
              >
                View the board
              </button>
            )}
          </>
        )}

        {phase === "failed" && (
          <>
            <AlertTriangle size={40} className="mx-auto text-amber-400" />
            <h1 className="mt-5 text-xl font-bold tracking-wide text-white">Payment did not complete</h1>
            <p className="mt-3 text-sm text-slate-400">
              Dodo did not capture this charge, so nothing was added to the board. Try TRANSMIT again
              with a supported test card (USD). You were not listed.
            </p>
            <div data-testid="confirm-failed" className="mono mt-4 inline-block rounded-md border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-[11px] tracking-[0.3em] text-amber-300">
              PAYMENT FAILED
            </div>
            <div data-testid="confirm-status" className="sr-only">failed</div>
            {onDismiss && (
              <button
                type="button"
                data-testid="confirm-retry"
                onClick={onDismiss}
                className="mono mt-5 flex items-center gap-2 rounded-md border border-gmh-cyan/40 bg-gmh-cyan/10 px-4 py-2 text-[11px] font-semibold tracking-wide text-gmh-cyan transition-colors hover:bg-gmh-cyan/20"
              >
                <RefreshCw size={12} /> Back to the board
              </button>
            )}
          </>
        )}

        {phase === "unknown" && (
          <>
            <AlertTriangle size={40} className="mx-auto text-amber-400" />
            <h1 className="mt-5 text-xl font-bold tracking-wide text-white">We could not find that session</h1>
            <p className="mt-3 text-sm text-slate-400">
              The payment session wasn't recognized by our server. If you were charged, your listing will appear
              once the bank confirms — please give it a moment, then refresh.
            </p>
            <div data-testid="confirm-status" className="sr-only">unknown</div>
            {onDismiss && (
              <button
                type="button"
                data-testid="confirm-retry"
                onClick={onDismiss}
                className="mono mt-5 flex items-center gap-2 rounded-md border border-gmh-cyan/40 bg-gmh-cyan/10 px-4 py-2 text-[11px] font-semibold tracking-wide text-gmh-cyan transition-colors hover:bg-gmh-cyan/20"
              >
                <RefreshCw size={12} /> Back to the board
              </button>
            )}
          </>
        )}

        {phase === "timeout" && (
          <>
            <AlertTriangle size={40} className="mx-auto text-amber-400" />
            <h1 className="mt-5 text-xl font-bold tracking-wide text-white">Still confirming</h1>
            <p className="mt-3 text-sm text-slate-400">
              We couldn't confirm your payment in time, but that doesn't mean it failed. Your listing will appear
              shortly once the bank settles — if it doesn't show up, please{" "}
              <a href="#/legal/contact" className="text-gmh-cyan underline">contact us</a>.
            </p>
            <div data-testid="confirm-timeout" className="mono mt-4 inline-block rounded-md border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-[11px] tracking-[0.3em] text-amber-300">
              ⏱ POLL TIMED OUT
            </div>
            <div data-testid="confirm-status" className="sr-only">timeout</div>
            {onDismiss && (
              <button
                type="button"
                data-testid="confirm-retry"
                onClick={onDismiss}
                className="mono mt-5 flex items-center gap-2 rounded-md border border-gmh-cyan/40 bg-gmh-cyan/10 px-4 py-2 text-[11px] font-semibold tracking-wide text-gmh-cyan transition-colors hover:bg-gmh-cyan/20"
              >
                <RefreshCw size={12} /> Back to the board
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Confirmation;
